import React, { useRef, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  IconButton,
  Modal,
  Backdrop,
  Fade
} from '@mui/material';
import {
  Schedule,
  CalendarToday,
  People,
  Analytics,
  ArrowBackIos,
  ArrowForwardIos,
  Close,
  ZoomIn
} from '@mui/icons-material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import { useTranslation } from '../../hooks/useTranslation';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const FeaturesSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const swiperRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  const features = [
    {
      icon: <Schedule sx={{ fontSize: 48, color: '#667eea' }} />,
      title: t('features.onlineBooking.title'),
      description: t('features.onlineBooking.description'),
      image: '/images/Reservations.png'
    },
    {
      icon: <CalendarToday sx={{ fontSize: 48, color: '#f093fb' }} />,
      title: t('features.calendarManagement.title'),
      description: t('features.calendarManagement.description'),
      image: '/images/GestionPlanning.png'
    },
    {
      icon: <People sx={{ fontSize: 48, color: '#4facfe' }} />,
      title: t('features.clientManagement.title'),
      description: t('features.clientManagement.description'),
      image: '/images/Dashboard.png'
    },
    {
      icon: <Analytics sx={{ fontSize: 48, color: '#43e97b' }} />,
      title: t('features.analytics.title'),
      description: t('features.analytics.description'),
      image: '/images/Dashboard.png'
    }
  ];

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev();
    }
  };

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext();
    }
  };

  const handleImageClick = (image, title) => {
    setSelectedImage({ src: image, title });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedImage(null);
  };

  return (
    <>
      <Box
        sx={{
          py: { xs: 2, md: 3 },
          backgroundColor: '#0a0a0a',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 1, md: 2 } }}>
          {/* Header */}
          <Box textAlign="center" mb={{ xs: 2, md: 3 }}>
            <Typography
              variant="h2"
              component="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                color: 'white',
                mb: 1,
                background: 'linear-gradient(45deg, #fff 30%, #f5f5dc 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {t('features.title')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '0.9rem', md: '1rem' },
                color: '#f5f5dc',
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
                opacity: 0.9
              }}
            >
              {t('features.subtitle')}
            </Typography>
          </Box>

          {/* Features Grid */}
          <Box sx={{ position: 'relative' }}>
            {/* Custom Navigation Buttons */}
            {!isMobile && (
              <>
                <IconButton
                  onClick={handlePrev}
                  sx={{
                    position: 'absolute',
                    left: -30,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    width: 45,
                    height: 45,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      transform: 'translateY(-50%) scale(1.1)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ArrowBackIos />
                </IconButton>
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: 'absolute',
                    right: -30,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    width: 45,
                    height: 45,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      transform: 'translateY(-50%) scale(1.1)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ArrowForwardIos />
                </IconButton>
              </>
            )}

            {/* Swiper Carousel */}
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              autoplay={{
                delay: 6000,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
              }}
              className="features-swiper"
              style={{ paddingBottom: '40px' }}
            >
              {features.map((feature, index) => (
                <SwiperSlide key={index}>
                  <Box
                    sx={{
                      backgroundColor: '#1a1a1a',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.1)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        borderColor: 'rgba(255,255,255,0.2)'
                      }
                    }}
                  >
                    <Box sx={{ height: { xs: 'auto', md: 400 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
                      {/* Image */}
                      <Box
                        sx={{
                          width: { xs: '100%', md: '60%' },
                          height: { xs: 200, md: 400 },
                          position: 'relative',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          '&:hover .zoom-overlay': {
                            opacity: 1
                          }
                        }}
                        onClick={() => handleImageClick(feature.image, feature.title)}
                      >
                        <Box
                          component="img"
                          src={feature.image}
                          alt={feature.title}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.05)'
                            }
                          }}
                        />
                        {/* Zoom Overlay */}
                        <Box
                          className="zoom-overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.3s ease'
                          }}
                        >
                          <ZoomIn sx={{ color: 'white', fontSize: 40 }} />
                        </Box>
                      </Box>

                      {/* Content */}
                      <Box
                        sx={{
                          width: { xs: '100%', md: '40%' },
                          p: { xs: 3, md: 4 },
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'flex-start'
                        }}
                      >
                        {/* Icon */}
                        <Box
                          sx={{
                            mb: 3,
                            display: 'inline-flex',
                            p: 2,
                            borderRadius: '50%',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            alignSelf: 'flex-start'
                          }}
                        >
                          {feature.icon}
                        </Box>

                        {/* Title */}
                        <Typography
                          variant="h3"
                          component="h3"
                          gutterBottom
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '1.5rem', md: '1.8rem' },
                            color: 'white',
                            mb: 2,
                            lineHeight: 1.2
                          }}
                        >
                          {feature.title}
                        </Typography>

                        {/* Description */}
                        <Typography
                          variant="body1"
                          sx={{
                            color: 'rgba(255,255,255,0.8)',
                            lineHeight: 1.6,
                            fontSize: { xs: '1rem', md: '1.1rem' }
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </SwiperSlide>
              ))}
            </Swiper>
          </Box>
        </Container>

        {/* Custom Styles */}
        <style jsx="true" global="true">{`
          .features-swiper .swiper-pagination {
            bottom: 10px !important;
          }
          
          .features-swiper .swiper-pagination-bullet {
            width: 10px !important;
            height: 10px !important;
            background: rgba(255,255,255,0.3) !important;
            opacity: 0.5 !important;
            transition: all 0.3s ease !important;
          }
          
          .features-swiper .swiper-pagination-bullet-active {
            opacity: 1 !important;
            background: white !important;
            transform: scale(1.2) !important;
          }
        `}</style>
      </Box>

      {/* Image Zoom Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
          sx: { backgroundColor: 'rgba(0,0,0,0.95)' }
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95vw', sm: '90vw', md: '80vw' },
              height: { xs: '90vh', sm: '85vh', md: '80vh' },
              outline: 'none',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 1, sm: 2 }
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: { xs: 10, sm: -40 },
                right: { xs: 10, sm: -10 },
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.9)'
                },
                zIndex: 1,
                width: { xs: 40, sm: 48 },
                height: { xs: 40, sm: 48 }
              }}
            >
              <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>

            {/* Image Container */}
            {selectedImage && (
              <Box
                sx={{
                  width: '100%',
                  height: { xs: 'calc(100% - 80px)', sm: 'calc(100% - 60px)' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                <Box
                  component="img"
                  src={selectedImage.src}
                  alt={selectedImage.title}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: { xs: 1, sm: 2 },
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    cursor: { xs: 'zoom-in', sm: 'default' },
                    transition: 'transform 0.3s ease',
                    '&:active': {
                      transform: { xs: 'scale(1.2)', sm: 'none' }
                    }
                  }}
                  onClick={(e) => {
                    // Sur mobile, permettre un zoom supplémentaire au tap
                    if (window.innerWidth < 600) {
                      const img = e.target;
                      if (img.style.transform === 'scale(1.5)') {
                        img.style.transform = 'scale(1)';
                      } else {
                        img.style.transform = 'scale(1.5)';
                      }
                    }
                  }}
                />
              </Box>
            )}

            {/* Title */}
            {selectedImage && (
              <Typography
                variant="h6"
                sx={{
                  color: 'white',
                  mt: { xs: 1, sm: 2 },
                  textAlign: 'center',
                  fontWeight: 500,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  px: 2
                }}
              >
                {selectedImage.title}
              </Typography>
            )}

            {/* Mobile Instructions */}
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                textAlign: 'center',
                mt: 1,
                display: { xs: 'block', sm: 'none' },
                fontSize: '0.75rem'
              }}
            >
              Tapez sur l'image pour zoomer davantage
            </Typography>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default FeaturesSection;